import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  Avatar,
  Button,
  Icon,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@/ui';
import { formatCadenceLine, timezoneAbbreviation, track } from '@/utils';

import type { CoachCadence, User } from '@/types';

import ScheduleSession from '@/components/Cal/scheduleSession';
import { coachIdentityQueryArg } from '@/features/appointments/coachIdentity';
import {
  legacySubjectPersonId,
  type MemberCoachBookingAuthority,
  type MemberCoachBookingState,
  resolveMemberCoachBookingAuthority,
  resolveMemberCoachBookingState,
  resolveQueryAuthorityState,
} from '@/features/appointments/memberCoachBookingState';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import {
  useGetAppointmentQuery,
  useGetAppointmentSchedulingContextQuery,
  useGetAppointmentsQuery,
} from '@/store/api/appointmentApi';
import { useGetMomentsFeatureFlagForLegacySubjectQuery } from '@/store/api/momentsApi';
import {
  useGetMomentsParticipantAppointmentQuery,
  useGetMomentsParticipantSchedulingContextQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';
import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';

// MEN-5021 overflow follow-up: below this card width the labelled secondary
// actions no longer fit on one line, so the card takes the compact icon-only
// treatment instead of wrapping onto a second row. Measured against the widest
// default CTA ("Book Bi-Weekly Sessions", 247px): 247 + 8 + Profile 116 + 8 +
// Message 132 = 511px of row content, plus the chrome between that row and the
// card edge -- 32px row padding, 16px text column padding, 80px avatar, 48px
// card padding -- is 687px. One threshold covers every default state: a card
// with no primary CTA compacts at the same width rather than keeping labels
// the neighbouring cards have already dropped.
const LABELLED_ACTIONS_MIN_CARD_WIDTH = 688;

const formatSeriesTime = (value: string, timezone: string) =>
  new Date(value)
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      hour12: true,
      minute: '2-digit',
      timeZone: timezone,
    })
    .replace(' ', '')
    .toLowerCase();

const formatSeriesTimeRange = (
  startAt: string,
  endAt: string,
  timezone: string,
) => {
  const start = formatSeriesTime(startAt, timezone);
  const end = formatSeriesTime(endAt, timezone);
  const startPeriod = start.slice(-2);
  const endPeriod = end.slice(-2);
  return `${startPeriod === endPeriod ? start.slice(0, -2) : start}-${end}`;
};

const formatContractEnd = (cadence: CoachCadence | undefined) => {
  if (
    cadence?.contractEnd?.displayMode !== 'contract_end' ||
    !cadence.contractEnd.date
  ) {
    return cadence?.contractEnd?.displayMode === 'ongoing' ? 'Ongoing' : null;
  }
  const end = new Date(`${cadence.contractEnd.date}T00:00:00Z`);
  const day = end.getUTCDate();
  let suffix = 'th';
  if (day % 10 === 1 && day % 100 !== 11) suffix = 'st';
  else if (day % 10 === 2 && day % 100 !== 12) suffix = 'nd';
  else if (day % 10 === 3 && day % 100 !== 13) suffix = 'rd';
  const month = end.toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
  return `Until ${month} ${day}${suffix}`;
};

export type CoachSessionState = MemberCoachBookingState;

export interface ActiveBiweeklySeries {
  bookingSource: string;
  seriesId: string;
}

interface Props {
  activeBiweekly?: ActiveBiweeklySeries;
  cadence?: CoachCadence;
  coach: User;
  compactSecondaryActions?: boolean;
  hasPreviousSessions?: boolean;
  memberCoachMatchState?: MemberCoachBookingAuthority['matchState'];
  now?: number;
  presentation?: 'default' | 'matched';
  refetch: () => void;
  onUpdateBiweekly?: (coach: User) => void;
  sessionState?: CoachSessionState;
  user: User;
}

const CoachCard: FC<Props> = ({
  activeBiweekly,
  cadence,
  coach,
  compactSecondaryActions = false,
  hasPreviousSessions = true,
  memberCoachMatchState = 'unavailable',
  now = Date.now(),
  onUpdateBiweekly,
  presentation = 'default',
  sessionState,
  user,
  refetch,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSchedule, setOpenSchedule] = useState<User | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);

  // MEN-5021 overflow follow-up: keyed to the width this card actually has, not
  // to the viewport. A single full-width card keeps its labelled actions; the
  // same card in a narrow Sessions column would wrap them onto a second line,
  // so it takes the compact treatment instead. `ResizeObserver` is absent in
  // jsdom, where the card stays labelled -- the same guard `WeekView` uses.
  useEffect(() => {
    const element = cardRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      setCardWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const {
    data: retainedCoachIdentity,
    currentData: coachIdentity,
    isError: isCoachIdentityError,
    isFetching: isCoachIdentityFetching,
    isSuccess: isCoachIdentitySuccess,
  } = useGetMomentsFeatureFlagForLegacySubjectQuery(
    coachIdentityQueryArg(coach.id),
  );
  const {
    data: appointmentPage,
    currentData: currentAppointmentPage,
    isError: isAppointmentsError,
    isFetching: isAppointmentsFetching,
    isSuccess: isAppointmentsSuccess,
  } = useGetAppointmentsQuery(user.id ? { userId: user.id } : skipToken);
  const isMomentsScheduled = usesMomentsScheduling(coach);
  const expectsNativeSeries = activeBiweekly?.bookingSource === 'moments';
  const bookingRouteDecision = useParticipantAppointmentRouteDecision({
    coachExternalId: coach.id || undefined,
    flow: 'booking',
  });
  const bookingTransport =
    bookingRouteDecision.state === 'ready'
      ? bookingRouteDecision.transport
      : undefined;
  const canLoadNativeSeries =
    expectsNativeSeries && bookingTransport !== undefined;
  const {
    currentData: schedulingContext,
    isError: isSchedulingContextError,
    isFetching: isSchedulingContextFetching,
    isLoading: isSchedulingContextLoading,
  } = useGetAppointmentSchedulingContextQuery(
    canLoadNativeSeries && bookingTransport === 'rails'
      ? {
          coachId: coach.id,
          kind: 'coaching',
          memberId: user.id,
          viewerTimezone:
            Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          visibleWeeks: 2,
        }
      : skipToken,
  );
  const momentsSchedulingContextQuery =
    useGetMomentsParticipantSchedulingContextQuery(
      canLoadNativeSeries && bookingTransport === 'moments'
        ? {
            counterpart: { externalId: coach.id, provider: 'legacy_app' },
            kind: 'coaching',
            viewerTimezone:
              Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            visibleWeeks: 2,
          }
        : skipToken,
    );
  const selectedSchedulingContext =
    bookingTransport === 'moments'
      ? momentsSchedulingContextQuery.currentData
      : schedulingContext;
  const matchingActiveSeries =
    selectedSchedulingContext?.activeSeries &&
    selectedSchedulingContext.activeSeries.id === activeBiweekly?.seriesId
      ? selectedSchedulingContext.activeSeries
      : undefined;
  const hasCurrentSchedulingContext =
    !!matchingActiveSeries &&
    !(bookingTransport === 'moments'
      ? momentsSchedulingContextQuery.isLoading
      : isSchedulingContextLoading) &&
    !(bookingTransport === 'moments'
      ? momentsSchedulingContextQuery.isFetching
      : isSchedulingContextFetching) &&
    !(bookingTransport === 'moments'
      ? momentsSchedulingContextQuery.isError
      : isSchedulingContextError);
  const {
    currentData: activeSeriesAppointment,
    isError: isActiveSeriesAppointmentError,
    isFetching: isActiveSeriesAppointmentFetching,
    isLoading: isActiveSeriesAppointmentLoading,
  } = useGetAppointmentQuery(
    canLoadNativeSeries &&
      hasCurrentSchedulingContext &&
      bookingTransport === 'rails'
      ? {
          appointmentId: matchingActiveSeries.nextAppointmentId,
          userId: user.id,
        }
      : skipToken,
  );
  const momentsActiveSeriesAppointmentQuery =
    useGetMomentsParticipantAppointmentQuery(
      canLoadNativeSeries &&
        hasCurrentSchedulingContext &&
        bookingTransport === 'moments'
        ? { appointmentId: matchingActiveSeries.nextAppointmentId }
        : skipToken,
    );
  const selectedActiveSeriesAppointment =
    bookingTransport === 'moments'
      ? momentsActiveSeriesAppointmentQuery.currentData
      : activeSeriesAppointment;
  const hasNativeSeriesIdentity =
    hasCurrentSchedulingContext &&
    !(bookingTransport === 'moments'
      ? momentsActiveSeriesAppointmentQuery.isLoading
      : isActiveSeriesAppointmentLoading) &&
    !(bookingTransport === 'moments'
      ? momentsActiveSeriesAppointmentQuery.isFetching
      : isActiveSeriesAppointmentFetching) &&
    !(bookingTransport === 'moments'
      ? momentsActiveSeriesAppointmentQuery.isError
      : isActiveSeriesAppointmentError) &&
    selectedActiveSeriesAppointment?.id ===
      matchingActiveSeries.nextAppointmentId &&
    selectedActiveSeriesAppointment.kind === 'coaching' &&
    selectedActiveSeriesAppointment.seriesId === activeBiweekly?.seriesId;
  const hasAuthoritativeAppointments =
    isAppointmentsSuccess && !isAppointmentsFetching;
  const biweeklyEnabled = isMomentsScheduled;
  const dashboardSessionState: CoachSessionState =
    sessionState ?? (hasPreviousSessions ? 'has_previous' : 'no_previous');
  const coachPersonId = retainedCoachIdentity?.subject.personId;
  const resolvedSessionState = resolveMemberCoachBookingState({
    appointments: appointmentPage?.appointments ?? [],
    coachPersonId,
    hasAuthoritativeAppointments,
    isCoachIdentityFetching,
    legacyState: dashboardSessionState,
    now,
  });
  const { canBookBiweekly } = resolveMemberCoachBookingAuthority(
    {
      appointmentState: resolveQueryAuthorityState({
        hasCurrentData: !!currentAppointmentPage,
        isError: isAppointmentsError,
        isFetching: isAppointmentsFetching,
        isSuccess: isAppointmentsSuccess,
      }),
      appointments: currentAppointmentPage?.appointments ?? [],
      coachPersonId: legacySubjectPersonId(coachIdentity),
      identityState: resolveQueryAuthorityState({
        hasCurrentData: !!coachIdentity,
        isError: isCoachIdentityError,
        isFetching: isCoachIdentityFetching,
        isSuccess: isCoachIdentitySuccess,
      }),
      matchState: memberCoachMatchState,
    },
    now,
  );
  const hasBookedChemistryCall = resolvedSessionState === 'upcoming_chemistry';
  const hasActiveBiweeklySeries = resolvedSessionState === 'active_biweekly';
  const useBiweeklyBooking =
    biweeklyEnabled &&
    resolvedSessionState === 'has_previous' &&
    canBookBiweekly;
  const useChemistryBooking =
    biweeklyEnabled && resolvedSessionState === 'no_previous';
  let scheduleFlow = 'cal';
  if (useBiweeklyBooking) scheduleFlow = 'biweekly';
  if (useChemistryBooking) scheduleFlow = 'chemistry';

  let scheduleLabel = 'Schedule';
  if (useBiweeklyBooking) scheduleLabel = 'Book Bi-Weekly Sessions';
  if (useChemistryBooking)
    scheduleLabel =
      presentation === 'matched'
        ? 'Book Chemistry Call'
        : 'Schedule Chemistry Call';
  if (hasActiveBiweeklySeries) scheduleLabel = 'Update Day and Time';

  const showScheduleButton =
    !hasBookedChemistryCall &&
    (resolvedSessionState !== 'has_previous' ||
      !biweeklyEnabled ||
      canBookBiweekly) &&
    (canLoadNativeSeries ||
      isMomentsScheduled ||
      !!coach.coachProfile?.calendarUrl);
  // MEN-5021: the choice prompt is bound to the Book Bi-Weekly Sessions CTA
  // itself, not to the card or to biweekly eligibility. `useBiweeklyBooking`,
  // `useChemistryBooking` and `hasActiveBiweeklySeries` partition
  // `resolvedSessionState`, so this predicate is exactly "that button is on
  // screen" — it stays false for the chemistry, active-series and legacy
  // labels. Both presentations render it: the established card is where state
  // 3 normally lands, and the matched card repeats it as a defensive fallback
  // so the CTA can never appear without the line it is tied to.
  const showBiweeklyChoicePrompt = showScheduleButton && useBiweeklyBooking;
  const appointmentBackedSeries = currentAppointmentPage?.appointments
    .filter((appointment) => {
      const endAt = Date.parse(appointment.currentSchedule.endAt);
      return (
        appointment.expectedIdentities.coach.personId === coachPersonId &&
        appointment.kind === 'coaching' &&
        appointment.seriesId !== null &&
        (!activeBiweekly || appointment.seriesId === activeBiweekly.seriesId) &&
        appointment.bookingStatus !== 'rejected' &&
        appointment.lifecycleStatus !== 'cancelled' &&
        (!Number.isFinite(endAt) || endAt >= now)
      );
    })
    .sort(
      (left, right) =>
        Date.parse(left.currentSchedule.startAt) -
        Date.parse(right.currentSchedule.startAt),
    )[0];
  const displayedSeriesSchedule = hasNativeSeriesIdentity
    ? matchingActiveSeries.nextSchedule
    : appointmentBackedSeries?.currentSchedule;
  const displayedCadenceWeeks = hasNativeSeriesIdentity
    ? matchingActiveSeries.cadenceWeeks
    : cadence?.intervalWeeks;
  let cadenceLine: string | undefined;
  let cadenceEnd: string | null | undefined;
  let mobileCadenceLine: string | undefined;
  if (displayedSeriesSchedule && displayedCadenceWeeks) {
    const start = new Date(displayedSeriesSchedule.startAt);
    const { timezone } = displayedSeriesSchedule;
    const weekday = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long',
    }).format(start);
    const timezoneLabel = timezoneAbbreviation(timezone, start);
    const interval =
      displayedCadenceWeeks === 1 ? 'week' : `${displayedCadenceWeeks} weeks`;
    const time = formatSeriesTimeRange(
      displayedSeriesSchedule.startAt,
      displayedSeriesSchedule.endAt,
      timezone,
    );
    cadenceLine = `Meeting every ${interval} on ${weekday}, ${time}${timezoneLabel ? ` ${timezoneLabel}` : ''}`;
    cadenceEnd = formatContractEnd(cadence);
    if (hasNativeSeriesIdentity) {
      const mobileTime = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
      }).format(start);
      mobileCadenceLine = `Every ${interval} on ${weekday} at ${mobileTime}${timezoneLabel ? ` ${timezoneLabel}` : ''}`;
    } else {
      mobileCadenceLine = cadence ? formatCadenceLine(cadence) : undefined;
    }
  } else if (!expectsNativeSeries || !isMomentsScheduled) {
    const formattedCadence = cadence ? formatCadenceLine(cadence) : undefined;
    mobileCadenceLine = formattedCadence;
    if (
      !hasActiveBiweeklySeries &&
      formattedCadence !== 'No upcoming sessions scheduled'
    ) {
      const [meeting, end] = formattedCadence?.split(' • ') ?? [];
      cadenceLine = meeting;
      cadenceEnd = end;
    }
  }

  const onProfile = () => {
    track('Clicked Coach Profile', {
      coachId: coach.id,
    });
    navigate(`/m/profiles-coach/${coach.id}`);
  };
  const onMessage = () => {
    track('Clicked Message Coach', {
      coachId: coach.id,
    });
    dispatch(setChatId(coach.cometChatId));
  };
  const onSchedule = () => {
    track('Clicked Schedule Button', {
      coachId: coach.id,
      flow: scheduleFlow,
    });
    if (hasActiveBiweeklySeries) {
      // MEN-4715: the legacy biweekly reschedule dialog calls
      // /api/v1/member_biweekly_bookings/:coachId/reschedule_options which
      // hits Moments' bookingseries.options endpoint. That endpoint is
      // disabled for a coach on Mento Calendar and returns 500 to the member.
      // Route recurring intent to the native series flow; that page resolves
      // and authorizes the current series before mutation.
      if (isMomentsScheduled) {
        navigate(`/m/book-biweekly/${coach.id}`, {
          state: { intent: 'reschedule-series' },
        });
        return;
      }
      onUpdateBiweekly?.(coach);
      return;
    }
    if (useBiweeklyBooking) {
      navigate(`/m/book-biweekly/${coach.id}`);
      return;
    }
    if (useChemistryBooking) {
      navigate(`/m/book-chemistry-call/${coach.id}`);
      return;
    }
    setOpenSchedule(coach);
  };

  const onCloseModal = () => {
    refetch();
    setOpenSchedule(null);
  };

  // A multi-card layout always compacts; a single card compacts only once it is
  // too narrow to show the labels on one line.
  // A measurement of 0 means the card has not been measured yet (or
  // `ResizeObserver` is unavailable), which stays labelled.
  const isNarrowForLabels =
    cardWidth > 0 && cardWidth < LABELLED_ACTIONS_MIN_CARD_WIDTH;
  const showCompactActions = compactSecondaryActions || isNarrowForLabels;

  // MEN-5021 overflow follow-up: the default action row is declared once and
  // mounted in one of two places. In compact (multi-card) mode it is a
  // full-width block under the avatar/text row, so it gets the whole card
  // instead of the narrower text column; otherwise it stays inside the text
  // column, aligned with the copy, exactly as the single full-width card has
  // always rendered it. `flex-wrap` is the floor: no width can clip an action,
  // it can only push it to a second line.
  const defaultActionRow = (
    <div
      className="mt-4 flex flex-wrap items-center gap-2 px-4"
      data-testid="coach-card-actions"
    >
      {showScheduleButton && (
        <Button
          startIcon={
            useBiweeklyBooking ? <Icon>calendar_today</Icon> : undefined
          }
          variant="contained"
          onClick={onSchedule}
        >
          {scheduleLabel}
        </Button>
      )}

      {/* MEN-5021: the secondary actions follow the primary CTA on the leading
          edge of the row. They are kept in one element so they wrap together
          rather than splitting across lines. */}
      <div
        className="flex items-center gap-2"
        data-testid="coach-card-secondary-actions"
      >
        {showCompactActions ? (
          <>
            <Tooltip title="View profile">
              <IconButton
                aria-label={`View ${coach.firstName} ${coach.lastName} profile`}
                className="size-[42px] shrink-0 rounded-xl border border-cement-300"
                variant="outlined"
                onClick={onProfile}
              >
                <Icon>account_box</Icon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Send message">
              <IconButton
                aria-label={`Message ${coach.firstName} ${coach.lastName}`}
                className="size-[42px] shrink-0 rounded-xl border border-cement-300"
                variant="outlined"
                onClick={onMessage}
              >
                <Icon>chat_bubble</Icon>
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              startIcon={<Icon>account_box</Icon>}
              variant="outlined"
              onClick={onProfile}
            >
              Profile
            </Button>
            <Button
              startIcon={<Icon>chat_bubble</Icon>}
              variant="outlined"
              onClick={onMessage}
            >
              Message
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* The observed element: `Paper` is a plain function component and does
          not forward a ref, so the card is measured through this wrapper. It
          adds no layout of its own -- `Paper` is already `w-full`. */}
      <div ref={cardRef} className="w-full">
        <Paper
          className="flex w-full rounded-xl p-6"
          elevation={presentation === 'matched' ? 1 : undefined}
          variant={presentation === 'matched' ? undefined : 'outlined'}
        >
          {presentation === 'matched' ? (
            <div className="flex w-full gap-6">
              <div className="hidden shrink-0 md:block">
                <Avatar
                  firstName={coach.firstName}
                  lastName={coach.lastName}
                  size="l"
                  src={coach.avatarUrl ?? ''}
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Typography className="select-none" variant="h6">
                  {coach.firstName} {coach.lastName}
                </Typography>
                <Typography
                  className="select-none text-cement-400"
                  variant="body2"
                >
                  {coach.profile?.bio}
                </Typography>
                {/* MEN-5021 review finding 1: `showBiweeklyChoicePrompt` is
                  presentation-agnostic, and this branch can independently reach
                  a bi-weekly CTA when dashboard state is unavailable. The
                  prompt is required wherever that CTA renders, so it is
                  repeated here as a defensive fallback. In the normal matched
                  state the flag is false and the bio treatment is untouched. */}
                {showBiweeklyChoicePrompt && (
                  <Typography
                    className="select-none font-bold text-cement-400"
                    variant="caption"
                  >
                    Choose {coach.firstName} as your coach:
                  </Typography>
                )}
                <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                  {showScheduleButton ? (
                    <Button
                      startIcon={
                        useChemistryBooking || useBiweeklyBooking ? (
                          <Icon>calendar_today</Icon>
                        ) : undefined
                      }
                      variant="contained"
                      onClick={onSchedule}
                    >
                      {scheduleLabel}
                    </Button>
                  ) : null}
                  <Tooltip title="View profile">
                    <IconButton
                      aria-label={`View ${coach.firstName} ${coach.lastName} profile`}
                      className="size-[42px] rounded-xl border border-cement-300"
                      variant="outlined"
                      onClick={onProfile}
                    >
                      <Icon>account_circle</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send message">
                    <IconButton
                      aria-label={`Message ${coach.firstName} ${coach.lastName}`}
                      className="size-[42px] rounded-xl border border-cement-300"
                      variant="outlined"
                      onClick={onMessage}
                    >
                      <Icon>chat_bubble</Icon>
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex w-full flex-col">
              <div className="flex">
                <div className="hidden md:block">
                  <Avatar
                    firstName={coach.firstName}
                    lastName={coach.lastName}
                    size="l"
                    src={coach.avatarUrl ?? ''}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col p-0 px-2">
                  <Typography className="mx-4 select-none" variant="h6">
                    {coach.firstName} {coach.lastName}
                  </Typography>
                  <Typography
                    className="mx-4 select-none text-cement-400 md:hidden"
                    variant="body2"
                  >
                    {coach.coachProfile?.role.length
                      ? `${coach.coachProfile.role}, `
                      : ''}
                    {coach.profile?.company}
                    {coach.profile?.pronouns ? (
                      <Typography variant="caption">
                        {' '}
                        ({coach.profile.pronouns})
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography
                    className="mx-4 select-none text-cement-400"
                    variant="body2"
                  >
                    {coach.coachProfile?.focus}
                  </Typography>
                  {cadenceLine && (
                    <Typography
                      className="mx-4 hidden select-none text-xl font-medium leading-8 md:block"
                      variant="body1"
                    >
                      {cadenceLine}
                      {cadenceEnd ? (
                        <span className="text-cement-400"> • {cadenceEnd}</span>
                      ) : null}
                    </Typography>
                  )}
                  {mobileCadenceLine && (
                    <Typography
                      className="mx-4 select-none text-cement-400 md:hidden"
                      variant="body2"
                    >
                      {mobileCadenceLine}
                    </Typography>
                  )}
                  {showBiweeklyChoicePrompt && (
                    <Typography
                      className="mx-4 mt-2 select-none font-bold text-cement-400"
                      variant="caption"
                    >
                      Choose {coach.firstName} as your coach:
                    </Typography>
                  )}
                  {!showCompactActions && defaultActionRow}
                </div>
              </div>
              {showCompactActions && defaultActionRow}
            </div>
          )}
        </Paper>
      </div>

      {openSchedule && (
        <ScheduleSession
          client={user}
          close={onCloseModal}
          coach={openSchedule}
        />
      )}
    </>
  );
};

export default CoachCard;
